import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Mail, Search, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

export const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, message: null });

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await api.get('/admin/messages');
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await api.patch(`/admin/messages/${messageId}/read`);
      fetchMessages();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await api.delete(`/admin/messages/${messageId}`);
      toast.success('Message supprimé');
      setDeleteDialog({ open: false, message: null });
      fetchMessages();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    if (!message.isRead) {
      markAsRead(message.id);
    }
  };

  const filteredMessages = messages.filter(message => 
    message.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const unreadCount = messages.filter(m => !m.isRead).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A96E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header - Mobile Responsive */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2C1810]">Messages de Contact</h1>
          {unreadCount > 0 && (
            <Badge className="bg-red-500 text-white">{unreadCount} non lu(s)</Badge>
          )}
        </div>
        <p className="text-sm md:text-base text-[#5D4037] mt-1">Gérez les messages envoyés via le formulaire de contact</p>
      </div>

      {/* Search - Mobile Optimized */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A67B5B]" size={20} />
            <Input
              placeholder="Rechercher par nom, email, sujet..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Messages List - Mobile Optimized */}
      <Card>
        <CardContent className="p-0">
          {filteredMessages.length > 0 ? (
            <div className="divide-y divide-[#E8DDD4]">
              {filteredMessages.map((message) => (
                <div
                  key={message.id}
                  className={`p-4 hover:bg-[#F5F0EB]/50 cursor-pointer transition-colors ${
                    !message.isRead ? 'bg-[#F5F0EB]' : ''
                  }`}
                  onClick={() => openMessage(message)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {!message.isRead && (
                          <div className="w-2 h-2 rounded-full bg-[#C9A96E] shrink-0"></div>
                        )}
                        <p className={`font-medium text-[#2C1810] text-sm md:text-base ${!message.isRead ? 'font-semibold' : ''}`}>
                          {message.name}
                        </p>
                      </div>
                      <p className="text-xs md:text-sm text-[#5D4037] truncate mt-0.5">{message.email}</p>
                      <p className={`text-sm md:text-base text-[#2C1810] mt-2 ${!message.isRead ? 'font-medium' : ''}`}>
                        {message.subject}
                      </p>
                      <p className="text-xs md:text-sm text-[#5D4037] line-clamp-2 mt-1">
                        {message.message}
                      </p>
                      <p className="text-xs text-[#A67B5B] mt-2">
                        {new Date(message.createdAt).toLocaleString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex sm:flex-col items-center sm:items-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, message }); }}
                        className="text-red-600 hover:text-red-700 border-red-300"
                      >
                        <Trash2 size={14} className="mr-1 sm:mr-0" />
                        <span className="sm:hidden">Supprimer</span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Mail size={48} className="mx-auto text-[#D4C4B5] mb-4" />
              <p className="text-[#5D4037]">Aucun message trouvé</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Message Detail Dialog - Mobile Optimized */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="w-[95vw] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-base md:text-lg pr-8">{selectedMessage?.subject}</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="p-4 bg-[#F5F0EB] rounded-lg">
                <p className="font-semibold text-[#2C1810]">{selectedMessage.name}</p>
                <p className="text-sm text-[#5D4037]">{selectedMessage.email}</p>
                {selectedMessage.phone && (
                  <p className="text-sm text-[#5D4037]">{selectedMessage.phone}</p>
                )}
              </div>
              
              <div className="p-4 bg-white border border-[#E8DDD4] rounded-lg">
                <p className="text-[#2C1810] whitespace-pre-wrap text-sm md:text-base">{selectedMessage.message}</p>
              </div>
              
              <p className="text-xs text-[#A67B5B]">
                Reçu le {new Date(selectedMessage.createdAt).toLocaleString('fr-FR')}
              </p>

              <div className="flex flex-col sm:flex-row gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => window.location.href = `mailto:${selectedMessage.email}`}
                  className="w-full"
                >
                  <Mail className="mr-2" size={16} />
                  Répondre par email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation - Mobile Optimized */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="w-[95vw] sm:w-full max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce message ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMessage(deleteDialog.message?.id)}
              className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
